import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Users } from "lucide-react";
import { Button } from "./ui/button";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const logoUrl = "https://customer-assets.emergentagent.com/job_romantic-brahmagupta/artifacts/o69ibm0p_image.png";

  const navigation = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Gallery", href: "/gallery" },
    { name: "Government Funding", href: "/funding" },
    { name: "Contact", href: "/contact" }
  ];

  const isActiveLink = (href) => {
    return location.pathname === href;
  };

  const handleBookTour = () => {
    // Navigate to contact page
    window.location.href = "/contact";
  };

  const handleReferFriend = () => {
    // Navigate to home page testimonials section and trigger modal
    if (window.location.pathname !== '/') {
      // If not on home page, go to home page with hash
      window.location.href = '/#testimonials-modal';
    } else {
      // If on home page, scroll to testimonials and open modal
      const testimonialSection = document.getElementById('testimonials');
      if (testimonialSection) {
        testimonialSection.scrollIntoView({ behavior: 'smooth' });
        
        // Wait for scroll to complete then open modal
        setTimeout(() => {
          const detailsButtons = document.querySelectorAll('button');
          const viewDetailsButton = Array.from(detailsButtons).find(button => 
            button.textContent && button.textContent.includes('View Full Details')
          );
          if (viewDetailsButton) {
            viewDetailsButton.click();
          }
        }, 1000);
      } else {
        // Fallback
        window.location.href = '/#testimonials';
      }
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50 border-b border-pink-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="h-16 w-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-105 bg-transparent">
              <img 
                src={logoUrl} 
                alt="Heathbourne House" 
                className="h-14 w-14 object-contain"
                style={{filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'}}
              />
            </div>
            <div className="flex flex-col">
              <div className="text-2xl font-bold whitespace-nowrap">
                <span style={{color: '#F8BBD9'}}>Heathbourne</span>
                <span style={{color: '#A7C7E7'}}> House</span>
              </div>
              <span className="text-base text-slate-800">Childcare</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`font-medium transition-colors relative ${
                  isActiveLink(item.href)
                    ? "text-blue-400"
                    : "text-slate-600 hover:text-blue-400"
                }`}
              >
                {item.name}
                {isActiveLink(item.href) && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-400 rounded-full"></div>
                )}
              </Link>
            ))}
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <Button 
              onClick={handleReferFriend}
              variant="outline"
              className="border-2 border-pink-400 text-pink-600 hover:bg-pink-400 hover:text-white px-6 py-2 rounded-full transition-colors text-sm font-medium flex items-center"
            >
              <Users className="h-4 w-4 mr-2" />
              Refer a Friend
            </Button>
            <Button 
              onClick={handleBookTour}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full transition-colors"
            >
              Book Tour
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-slate-600" />
            ) : (
              <Menu className="h-6 w-6 text-slate-600" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-pink-100">
          <div className="px-4 py-4 space-y-3">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-3 py-2 rounded-lg transition-colors ${
                  isActiveLink(item.href)
                    ? "bg-blue-50 text-blue-400"
                    : "text-slate-600 hover:bg-gray-50"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-3 border-t border-pink-100">
              <div className="space-y-2">
                <Button 
                  onClick={handleReferFriend}
                  variant="outline"
                  className="w-full border-2 border-pink-400 text-pink-600 hover:bg-pink-400 hover:text-white py-2 rounded-full"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Refer a Friend
                </Button>
                <Button 
                  onClick={handleBookTour}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-full"
                >
                  Book Tour
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;